import {
  Children,
  createContext,
  isValidElement,
  useContext,
  type ReactElement,
  type ReactNode,
} from 'react'
import {
  Tooltip as CanonicalTooltip,
  type TooltipPlacement,
} from '@juice/design-system'
import '@juice/design-system/styles.css'

const TooltipDelayContext = createContext(0)

export interface TooltipProviderProps {
  children: ReactNode
  delay?: number
}

function TooltipProvider({ children, delay = 0 }: TooltipProviderProps) {
  return (
    <TooltipDelayContext.Provider value={delay}>
      {children}
    </TooltipDelayContext.Provider>
  )
}

export interface TooltipTriggerProps {
  render: ReactElement
}

function TooltipTrigger(_props: TooltipTriggerProps) {
  return null
}

export interface TooltipContentProps {
  children: ReactNode
  side?: 'top' | 'bottom' | 'left' | 'right'
  align?: 'start' | 'center' | 'end'
  sideOffset?: number
  alignOffset?: number
  className?: string
}

function TooltipContent(_props: TooltipContentProps) {
  return null
}

export interface TooltipProps {
  children: ReactNode
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
}

function placementFor(
  side: TooltipContentProps['side'] = 'top',
  align: TooltipContentProps['align'] = 'center',
): TooltipPlacement {
  if (side === 'left' || side === 'right') return side
  return align === 'center' ? side : `${side}-${align}`
}

/**
 * Behavior-only bridge for the app's former compound Tooltip API. The bridge
 * only pairs trigger and content; the canonical Tooltip owns every visual,
 * positioning, delay, arrow, and accessibility contract.
 */
function Tooltip({
  children,
  open,
  defaultOpen,
  onOpenChange,
}: TooltipProps) {
  const delay = useContext(TooltipDelayContext)
  const parts = Children.toArray(children)
  const trigger = parts.find(
    (part): part is ReactElement<TooltipTriggerProps> =>
      isValidElement(part) && part.type === TooltipTrigger,
  )
  const content = parts.find(
    (part): part is ReactElement<TooltipContentProps> =>
      isValidElement(part) && part.type === TooltipContent,
  )

  if (!trigger || !content) return null

  return (
    <CanonicalTooltip
      className={content.props.className}
      content={content.props.children}
      defaultOpen={defaultOpen}
      delay={delay}
      onOpenChange={onOpenChange}
      open={open}
      placement={placementFor(content.props.side, content.props.align)}
    >
      {trigger.props.render}
    </CanonicalTooltip>
  )
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
